import React, { useState, useEffect } from 'react';
import { auth, db } from '../firebase/config';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import {
  verifyBeforeUpdateEmail, updatePassword, reauthenticateWithCredential,
  EmailAuthProvider, signOut
} from 'firebase/auth';
import CertificateGenerator from './CertificateGenerator';

export default function AccountSettings({ user, onNav }) {
  const [profile, setProfile] = useState({ firstName: '', lastName: '', jobTitle: '', department: '' });
  const [engagementCode, setEngagementCode] = useState(null);
  const [emailForm, setEmailForm] = useState({ newEmail: '', confirmPassword: '' });
  const [passwordForm, setPasswordForm] = useState({ currentPassword: '', newPassword: '', confirmPassword: '' });
  const [profileMsg, setProfileMsg] = useState(null);
  const [emailMsg, setEmailMsg] = useState(null);
  const [passwordMsg, setPasswordMsg] = useState(null);
  const [saving, setSaving] = useState({ profile: false, email: false, password: false });

  useEffect(() => {
    const load = async () => {
      const snap = await getDoc(doc(db, 'users', user.uid));
      if (snap.exists()) {
        const d = snap.data();
        setProfile({
          firstName: d.firstName || '',
          lastName: d.lastName || '',
          jobTitle: d.jobTitle || '',
          department: d.department || '',
        });
        // Grab the engagement code so CertificateGenerator can look up program/facilitator info
        setEngagementCode(d.companyCode || d.engagementId || null);
      }
    };
    load();
  }, [user.uid]);

  const showMsg = (setter, type, text) => {
    setter({ type, text });
    setTimeout(() => setter(null), 4000);
  };

  const handleSaveProfile = async () => {
    setSaving(s => ({ ...s, profile: true }));
    try {
      await updateDoc(doc(db, 'users', user.uid), {
        firstName: profile.firstName,
        lastName: profile.lastName,
        displayName: `${profile.firstName} ${profile.lastName}`.trim(),
        jobTitle: profile.jobTitle,
        department: profile.department,
      });
      showMsg(setProfileMsg, 'success', 'Profile updated.');
    } catch {
      showMsg(setProfileMsg, 'error', 'Could not save. Please try again.');
    }
    setSaving(s => ({ ...s, profile: false }));
  };

  const handleUpdateEmail = async () => {
    if (!emailForm.newEmail || !emailForm.confirmPassword) {
      showMsg(setEmailMsg, 'error', 'Fill in both fields.');
      return;
    }
    setSaving(s => ({ ...s, email: true }));
    try {
      const credential = EmailAuthProvider.credential(user.email, emailForm.confirmPassword);
      await reauthenticateWithCredential(user, credential);
      await verifyBeforeUpdateEmail(user, emailForm.newEmail);
      showMsg(setEmailMsg, 'success', 'A verification link has been sent to your new address. Your email will update once you click it.');
      setEmailForm({ newEmail: '', confirmPassword: '' });
    } catch (err) {
      if (err.code === 'auth/wrong-password') showMsg(setEmailMsg, 'error', 'Incorrect password.');
      else if (err.code === 'auth/email-already-in-use') showMsg(setEmailMsg, 'error', 'That email is already in use.');
      else if (err.code === 'auth/invalid-email') showMsg(setEmailMsg, 'error', 'That email address is not valid.');
      else showMsg(setEmailMsg, 'error', 'Could not update email. Please try again.');
    }
    setSaving(s => ({ ...s, email: false }));
  };

  const handleUpdatePassword = async () => {
    if (!passwordForm.currentPassword || !passwordForm.newPassword || !passwordForm.confirmPassword) {
      showMsg(setPasswordMsg, 'error', 'Fill in all three fields.');
      return;
    }
    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      showMsg(setPasswordMsg, 'error', 'New passwords do not match.');
      return;
    }
    if (passwordForm.newPassword.length < 8) {
      showMsg(setPasswordMsg, 'error', 'New password must be at least 8 characters.');
      return;
    }
    setSaving(s => ({ ...s, password: true }));
    try {
      const credential = EmailAuthProvider.credential(user.email, passwordForm.currentPassword);
      await reauthenticateWithCredential(user, credential);
      await updatePassword(user, passwordForm.newPassword);
      showMsg(setPasswordMsg, 'success', 'Password updated. Signing you out now.');
      setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
      setTimeout(() => signOut(auth), 2000);
    } catch (err) {
      if (err.code === 'auth/wrong-password') showMsg(setPasswordMsg, 'error', 'Current password is incorrect.');
      else showMsg(setPasswordMsg, 'error', 'Could not update password. Please try again.');
    }
    setSaving(s => ({ ...s, password: false }));
  };

  const setP = (k, v) => setProfile(prev => ({ ...prev, [k]: v }));
  const setE = (k, v) => setEmailForm(prev => ({ ...prev, [k]: v }));
  const setPw = (k, v) => setPasswordForm(prev => ({ ...prev, [k]: v }));

  return (
    <div style={s.wrapper}>
      <div style={s.header}>
        <div style={s.dayTag}>Account</div>
        <div style={s.title}>Account Settings</div>
      </div>

      {/* CERTIFICATE SECTION — only renders if earned */}
      {engagementCode && (
        <CertificateGenerator user={user} engagementCode={engagementCode} />
      )}

      <div style={s.section}>
        <div style={s.sectionTitle}>Profile Information</div>
        <div style={s.row}>
          <div style={s.field}>
            <label style={s.label}>First Name</label>
            <input style={s.input} type="text" value={profile.firstName} onChange={e => setP('firstName', e.target.value)} />
          </div>
          <div style={s.field}>
            <label style={s.label}>Last Name</label>
            <input style={s.input} type="text" value={profile.lastName} onChange={e => setP('lastName', e.target.value)} />
          </div>
        </div>
        <div style={s.field}>
          <label style={s.label}>Job Title</label>
          <input style={s.input} type="text" value={profile.jobTitle} onChange={e => setP('jobTitle', e.target.value)} placeholder="e.g. HR Manager" />
        </div>
        <div style={s.field}>
          <label style={s.label}>Department</label>
          <input style={s.input} type="text" value={profile.department} onChange={e => setP('department', e.target.value)} placeholder="e.g. HR, Marketing, Finance" />
        </div>
        <button style={s.saveBtn} onClick={handleSaveProfile} disabled={saving.profile}>
          {saving.profile ? 'Saving...' : 'Save Profile'}
        </button>
        {profileMsg && <div style={{ ...s.msg, ...(profileMsg.type === 'success' ? s.msgSuccess : s.msgError) }}>{profileMsg.text}</div>}
      </div>

      <div style={s.section}>
        <div style={s.sectionTitle}>Email Address</div>
        <div style={s.field}>
          <label style={s.label}>Current Email</label>
          <input style={{ ...s.input, background: '#F2F0EC', color: '#8A8A8A' }} type="email" value={user.email} disabled />
        </div>
        <div style={s.field}>
          <label style={s.label}>New Email</label>
          <input style={s.input} type="email" placeholder="your@newemail.com" value={emailForm.newEmail} onChange={e => setE('newEmail', e.target.value)} />
        </div>
        <div style={s.field}>
          <label style={s.label}>Current Password</label>
          <input style={s.input} type="password" placeholder="Enter your current password to confirm" value={emailForm.confirmPassword} onChange={e => setE('confirmPassword', e.target.value)} />
        </div>
        <button style={s.saveBtn} onClick={handleUpdateEmail} disabled={saving.email}>
          {saving.email ? 'Updating...' : 'Update Email'}
        </button>
        <div style={s.note}>A verification email will be sent to your new address. Your email will update once you click the link.</div>
        {emailMsg && <div style={{ ...s.msg, ...(emailMsg.type === 'success' ? s.msgSuccess : s.msgError) }}>{emailMsg.text}</div>}
      </div>

      <div style={s.section}>
        <div style={s.sectionTitle}>Password</div>
        <div style={s.field}>
          <label style={s.label}>Current Password</label>
          <input style={s.input} type="password" placeholder="••••••••" value={passwordForm.currentPassword} onChange={e => setPw('currentPassword', e.target.value)} />
        </div>
        <div style={s.field}>
          <label style={s.label}>New Password</label>
          <input style={s.input} type="password" placeholder="At least 8 characters" value={passwordForm.newPassword} onChange={e => setPw('newPassword', e.target.value)} />
        </div>
        <div style={s.field}>
          <label style={s.label}>Confirm New Password</label>
          <input style={s.input} type="password" placeholder="Confirm new password" value={passwordForm.confirmPassword} onChange={e => setPw('confirmPassword', e.target.value)} />
        </div>
        <button style={s.saveBtn} onClick={handleUpdatePassword} disabled={saving.password}>
          {saving.password ? 'Updating...' : 'Update Password'}
        </button>
        <div style={s.note}>You will be signed out automatically after changing your password.</div>
        {passwordMsg && <div style={{ ...s.msg, ...(passwordMsg.type === 'success' ? s.msgSuccess : s.msgError) }}>{passwordMsg.text}</div>}
      </div>
    </div>
  );
}

const s = {
  wrapper: { maxWidth: '600px', paddingBottom: '60px', fontFamily: 'DM Sans, sans-serif' },
  header: { background: '#1B2A4A', padding: '24px 28px', marginBottom: '20px' },
  dayTag: { fontSize: '10px', fontWeight: '600', letterSpacing: '2px', textTransform: 'uppercase', color: '#D4A847', marginBottom: '6px' },
  title: { fontFamily: 'Playfair Display, serif', fontSize: '22px', color: '#fff' },
  section: { background: '#FFFFFF', border: '1.5px solid #E8E6E2', padding: '24px', marginBottom: '14px' },
  sectionTitle: { fontFamily: 'Playfair Display, serif', fontSize: '16px', color: '#1B2A4A', marginBottom: '16px', paddingBottom: '10px', borderBottom: '1px solid #E8E6E2' },
  row: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' },
  field: { marginBottom: '14px' },
  label: { display: 'block', fontSize: '11px', fontWeight: '600', letterSpacing: '1px', textTransform: 'uppercase', color: '#8A8A8A', marginBottom: '5px', fontFamily: 'DM Sans, sans-serif' },
  input: { width: '100%', border: '1.5px solid #E8E6E2', padding: '10px 14px', fontFamily: 'DM Sans, sans-serif', fontSize: '13px', color: '#3D3D3D', background: '#FAF8F5', outline: 'none', boxSizing: 'border-box' },
  saveBtn: { background: '#1B2A4A', color: '#fff', border: 'none', padding: '10px 24px', fontFamily: 'Playfair Display, serif', fontSize: '13px', fontWeight: '700', cursor: 'pointer' },
  note: { fontSize: '11px', color: '#8A8A8A', marginTop: '10px', fontStyle: 'italic', lineHeight: '1.6' },
  msg: { padding: '8px 14px', fontSize: '12px', fontWeight: '500', marginTop: '10px' },
  msgSuccess: { background: '#eaf3de', color: '#3b6d11' },
  msgError: { background: '#fdecea', color: '#a32d2d' },
};
